/**
 * PATH: apps/qa/src/lib/api/floors-areas.ts
 * PURPOSE: Floors and Areas CRUD + reorder API for QA inspections
 */

import { api, normaliseList } from './helpers'

// ── Types ────────────────────────────────────────────────────────────────────

export interface FloorItem {
  id: string
  inspection: string
  floor_name: string
  sort_order: number
  created_at: string
  areas: AreaItem[]
}

export interface AreaItem {
  id: string
  floor: string
  area_name: string
  area_code: string
  sort_order: number
  status: string
  locked_at: string | null
  locked_by: string | null
  locked_by_name: string | null
  lock_reason: string
  created_at: string
}

export interface ReorderItem {
  id: string
  sort_order: number
}

// ── Floor API ────────────────────────────────────────────────────────────────

export const floorApi = {
  getFloors: async (params: { inspection_id: string; page?: number; page_size?: number }): Promise<{ results: FloorItem[]; count: number }> => {
    if (!params.inspection_id) { console.error('[floorApi.getFloors] inspection_id is required'); return { results: [], count: 0 } }
    const response = await api().get('/qa/floors/', { params })
    return normaliseList<FloorItem>(response.data)
  },

  createFloor: async (data: { inspection: string; floor_name: string; sort_order?: number }): Promise<FloorItem> => {
    const response = await api().post('/qa/floors/', data)
    return response.data
  },

  updateFloor: async (id: string, data: { floor_name?: string; sort_order?: number }): Promise<FloorItem> => {
    const response = await api().patch(`/qa/floors/${id}/`, data)
    return response.data
  },

  deleteFloor: async (id: string): Promise<void> => {
    await api().delete(`/qa/floors/${id}/`)
  },

  reorder: async (order: ReorderItem[]): Promise<{ status: string; updated: number }> => {
    const response = await api().post('/qa/floors/reorder/', { order })
    return response.data
  },

  cloneFloor: async (id: string, newFloorName?: string): Promise<FloorItem> => {
    const response = await api().post(`/qa/floors/${id}/clone/`, { new_floor_name: newFloorName })
    return response.data
  },
}

// ── Area API ─────────────────────────────────────────────────────────────────

export const areaApi = {
  getAreas: async (params: { inspection_id?: string; floor_id?: string; status?: string; page?: number; page_size?: number }): Promise<{ results: AreaItem[]; count: number }> => {
    if (!params.inspection_id && !params.floor_id) { console.error('[areaApi.getAreas] Either inspection_id or floor_id is required'); return { results: [], count: 0 } }
    const response = await api().get('/qa/areas/', { params })
    return normaliseList<AreaItem>(response.data)
  },

  createArea: async (data: { floor: string; area_name: string; area_code?: string; sort_order?: number }): Promise<AreaItem> => {
    const response = await api().post('/qa/areas/', data)
    return response.data
  },

  updateArea: async (id: string, data: { area_name?: string; area_code?: string; sort_order?: number }): Promise<AreaItem> => {
    const response = await api().patch(`/qa/areas/${id}/`, data)
    return response.data
  },

  deleteArea: async (id: string): Promise<void> => {
    await api().delete(`/qa/areas/${id}/`)
  },

  reorder: async (order: ReorderItem[]): Promise<{ status: string; updated: number }> => {
    const response = await api().post('/qa/areas/reorder/', { order })
    return response.data
  },
}
